import { type HTMLAttributes, forwardRef } from 'react';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  text: string;
  color?: string;
  variant?: 'filled' | 'outlined';
  size?: 'sm' | 'md';
}

const sizeClasses: Record<NonNullable<BadgeProps['size']>, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ text, color, variant = 'filled', size = 'sm', className = '', style, ...props }, ref) => {
    const customStyle: React.CSSProperties = { ...style };

    if (color) {
      if (variant === 'filled') {
        customStyle.backgroundColor = color;
        customStyle.color = getContrastTextColor(color);
      } else {
        customStyle.borderColor = color;
        customStyle.color = color;
      }
    }

    return (
      <span
        ref={ref}
        className={[
          'inline-flex items-center font-medium rounded-full whitespace-nowrap',
          'transition-colors duration-default ease-default',
          sizeClasses[size],
          variant === 'filled' && !color
            ? 'bg-primary-light text-text-primary'
            : '',
          variant === 'outlined'
            ? 'bg-transparent border'
            : '',
          variant === 'outlined' && !color
            ? 'border-border text-text-secondary'
            : '',
          className,
        ].join(' ')}
        style={customStyle}
        {...props}
      >
        {text}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

/**
 * Returns black or white text color based on background luminance
 * for sufficient contrast.
 */
function getContrastTextColor(hex: string): string {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  // Relative luminance formula (simplified)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
}
